import {
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
} from '@tanstack/react-query';

import { API_MODULES } from '@lib/api/constants';
import DIContainer from '@lib/dicontainer';
import { CatalogFilters, CollectionInput } from '@lib/types';
import { GameFilters, GameInput, GameUpdate } from '@lib/types/game';

const usecase = DIContainer.getCatalogUseCase();
const artworkUseCase = DIContainer.getCatalogArtworkUseCase();

const MINIMUM_CATALOG_SEARCH_LENGTH = 2;
const REFERENCE_DATA_STALE_TIME = 5 * 60 * 1000;
const LANDING_CATALOG_STALE_TIME = 60 * 1000;

export const catalogKey = [API_MODULES.CATALOG];

export const landingCatalogOptions = () =>
  queryOptions({
    queryKey: [...catalogKey, 'landing'],
    queryFn: ({ signal }) => usecase.landingCatalog(signal),
    staleTime: LANDING_CATALOG_STALE_TIME,
  });

export const cardArtworksOptions = (cardId?: string, collectionId?: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'admin-artworks', cardId, collectionId ?? null],
    queryFn: ({ signal }) =>
      artworkUseCase.listArtworks(cardId ?? '', collectionId, signal),
    enabled: Boolean(cardId),
  });

export const importArtworkManifestOptions = () =>
  mutationOptions({
    mutationFn: (file: File) => artworkUseCase.importArtworkManifest(file),
  });

export const exportArtworkManifestOptions = () =>
  mutationOptions({ mutationFn: () => artworkUseCase.exportArtworkManifest() });

export const setArtworkExternalReferenceOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      externalId,
    }: {
      cardId: string;
      externalId: string;
    }) => artworkUseCase.setArtworkExternalReference(cardId, externalId),
  });

export const synchronizeArtworksOptions = () =>
  mutationOptions({
    mutationFn: (cardId: string) => artworkUseCase.synchronizeArtworks(cardId),
  });

export const uploadArtworkOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      file,
      isPrimary,
    }: {
      cardId: string;
      file: File;
      isPrimary?: boolean;
    }) => artworkUseCase.uploadArtwork(cardId, file, isPrimary),
  });

export const setPrimaryArtworkOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      artworkId,
    }: {
      cardId: string;
      artworkId: string;
    }) => artworkUseCase.setPrimaryArtwork(cardId, artworkId),
  });

export const setArtworkEnabledOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      artworkId,
      enabled,
    }: {
      cardId: string;
      artworkId: string;
      enabled: boolean;
    }) => artworkUseCase.setArtworkEnabled(cardId, artworkId, enabled),
  });

export const deleteArtworkOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      artworkId,
    }: {
      cardId: string;
      artworkId: string;
    }) => artworkUseCase.deleteArtwork(cardId, artworkId),
  });

export const setPrintingArtworkOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      printingId,
      artworkId,
      rarityId,
      code,
      isAlternateArt,
    }: {
      cardId: string;
      printingId: string;
      artworkId?: string | null;
      rarityId?: string;
      code?: string;
      isAlternateArt?: boolean;
    }) =>
      artworkUseCase.setPrintingArtwork(cardId, printingId, {
        artworkId,
        rarityId,
        code,
        isAlternateArt,
      }),
  });

export const createPrintingOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      collectionId,
      rarityId,
      code,
      isAlternateArt,
      artworkId,
    }: {
      cardId: string;
      collectionId: string;
      rarityId: string;
      code: string;
      isAlternateArt?: boolean;
      artworkId?: string | null;
    }) =>
      artworkUseCase.createPrinting(cardId, {
        collectionId,
        rarityId,
        code,
        isAlternateArt,
        artworkId,
      }),
  });

export const deletePrintingOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      printingId,
    }: {
      cardId: string;
      printingId: string;
    }) => artworkUseCase.deletePrinting(cardId, printingId),
  });

export const reorderPrintingsOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      printingIds,
    }: {
      cardId: string;
      printingIds: string[];
    }) => artworkUseCase.reorderPrintings(cardId, printingIds),
  });

export const gamesOptions = () =>
  queryOptions({
    queryKey: [...catalogKey, 'games'],
    queryFn: ({ signal }) => usecase.games(signal),
    staleTime: 0,
  });

export const collectionsOptions = (gameCode: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'collections', gameCode],
    queryFn: ({ signal }) => usecase.collections(gameCode, signal),
    enabled: Boolean(gameCode),
    staleTime: REFERENCE_DATA_STALE_TIME,
  });

export const adminCollectionsByGameCodeOptions = (gameCode: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'admin-collections', gameCode],
    queryFn: ({ signal }) => usecase.adminCollections(gameCode, signal),
    enabled: Boolean(gameCode),
    staleTime: REFERENCE_DATA_STALE_TIME,
  });

export const raritiesOptions = () =>
  queryOptions({
    queryKey: [...catalogKey, 'rarities'],
    queryFn: ({ signal }) => usecase.rarities(signal),
    staleTime: REFERENCE_DATA_STALE_TIME,
  });

export const languagesOptions = () =>
  queryOptions({
    queryKey: [...catalogKey, 'languages'],
    queryFn: ({ signal }) => usecase.languages(signal),
    staleTime: REFERENCE_DATA_STALE_TIME,
  });

export const catalogFilterOptions = (gameCode?: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'filters', gameCode],
    queryFn: async ({ signal }) => {
      const [collections, rarities] = await Promise.all([
        gameCode ? usecase.collections(gameCode, signal) : Promise.resolve([]),
        usecase.rarities(signal),
      ]);

      return { collections, rarities };
    },
    staleTime: REFERENCE_DATA_STALE_TIME,
  });

export const catalogSynchronizationOptions = () =>
  queryOptions({
    queryKey: [...catalogKey, 'synchronization'],
    queryFn: ({ signal }) => usecase.latestSynchronization(signal),
  });

export const cardDetailsOptions = (gameCode: string, cardSlug: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'details', gameCode, cardSlug],
    queryFn: ({ signal }) => usecase.details(gameCode, cardSlug, signal),
  });

export const cardOptions = (cardId: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'options', cardId],
    queryFn: ({ signal }) => usecase.options(cardId, signal),
  });

export const catalogOptions = (filters: CatalogFilters = {}) =>
  queryOptions({
    queryKey: [...catalogKey, 'list', filters],
    queryFn: async ({ signal }) => (await usecase.list(filters, signal)).items,
  });

export const catalogSearchOptions = (search: string, limit?: number) =>
  queryOptions({
    queryKey: [...catalogKey, 'search', search, limit],
    queryFn: ({ signal }) => usecase.search({ search, limit }, signal),
    enabled: search.length >= MINIMUM_CATALOG_SEARCH_LENGTH,
  });

export const catalogPreviewOptions = (search: string, gameCode?: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'preview', gameCode, search],
    queryFn: ({ signal }) => usecase.preview(search, gameCode, signal),
    enabled: search.length >= MINIMUM_CATALOG_SEARCH_LENGTH,
  });

export const collectionCatalogOptions = (collectionId: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'collection', collectionId, 'all'],
    queryFn: ({ signal }) => usecase.allCollectionCards(collectionId, signal),
  });

export const collectionCatalogInfiniteOptions = (
  collectionId: string,
  initialPage = 1,
) =>
  infiniteQueryOptions({
    queryKey: [...catalogKey, 'collection', collectionId, initialPage],
    initialPageParam: initialPage,
    queryFn: ({ signal, pageParam }) =>
      usecase.collectionCards(collectionId, undefined, signal, pageParam),
    getNextPageParam: (page, _pages, pageParam) =>
      page.nextCursor ? pageParam + 1 : undefined,
    getPreviousPageParam: (_page, _pages, pageParam) =>
      pageParam > 1 ? pageParam - 1 : undefined,
    enabled: Boolean(collectionId),
  });

export const catalogInfiniteOptions = (
  filters: CatalogFilters = {},
  initialPage = 1,
) =>
  infiniteQueryOptions({
    queryKey: [...catalogKey, 'infinite', filters, initialPage],
    initialPageParam: initialPage,
    queryFn: ({ signal, pageParam }) =>
      usecase.list({ ...filters, page: pageParam }, signal),
    getNextPageParam: (page, _pages, pageParam) =>
      page.nextCursor ? pageParam + 1 : undefined,
    getPreviousPageParam: (_page, _pages, pageParam) =>
      pageParam > 1 ? pageParam - 1 : undefined,
  });

export const adminCatalogInfiniteOptions = (filters: CatalogFilters = {}) =>
  infiniteQueryOptions({
    queryKey: [...catalogKey, 'admin-cards', filters],
    initialPageParam: '',
    queryFn: ({ signal, pageParam }) =>
      usecase.adminCards(
        { ...filters, cursor: pageParam || undefined },
        signal,
      ),
    getNextPageParam: (page) => page.nextCursor,
  });

export const syncCatalogOptions = () =>
  mutationOptions({ mutationFn: () => usecase.synchronize() });

export const adminRaritiesOptions = () =>
  queryOptions({
    queryKey: [...catalogKey, 'admin-rarities'],
    queryFn: ({ signal }) => usecase.listRarities(signal),
  });

export const createRarityOptions = () =>
  mutationOptions({
    mutationFn: (data: {
      name: string;
      shortName?: string | null;
      code: string;
    }) => usecase.createRarity(data),
  });

export const updateRarityOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name: string;
      shortName?: string | null;
      code: string;
    }) => usecase.updateRarity(id, data),
  });

export const deleteRarityOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => usecase.deleteRarity(id),
  });

export const adminCollectionsOptions = () =>
  queryOptions({
    queryKey: [...catalogKey, 'admin-collections'],
    queryFn: ({ signal }) => usecase.listCollections(signal),
  });

export const createCollectionOptions = () =>
  mutationOptions({
    mutationFn: (data: CollectionInput) => usecase.createCollection(data),
  });

export const updateCollectionOptions = () =>
  mutationOptions({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      gameId?: string;
      name?: string;
      code?: string;
      marketplaceVisible?: boolean;
      preReleaseDate?: string | null;
      releaseDate?: string | null;
    }) => usecase.updateCollection(id, data),
  });

export const deleteCollectionOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => usecase.deleteCollection(id),
  });

export const createCardOptions = () =>
  mutationOptions({
    mutationFn: (data: import('@lib/types').CreateCardInput) =>
      usecase.createCard(data),
  });

export const adminCardOptions = (cardId: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'admin-card', cardId],
    queryFn: ({ signal }) => usecase.adminCard(cardId, signal),
    enabled: Boolean(cardId),
  });

export const updateCardOptions = () =>
  mutationOptions({
    mutationFn: ({
      cardId,
      ...data
    }: import('@lib/types').UpdateCardInput & { cardId: string }) =>
      usecase.updateCard(cardId, data),
  });

export const deleteCardOptions = () =>
  mutationOptions({
    mutationFn: (cardId: string) => usecase.deleteCard(cardId),
  });

export const rarityCardsOptions = (rarityId?: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'rarity-cards', rarityId],
    queryFn: async ({ signal }) => {
      if (!rarityId) {
        return [];
      }

      return (await usecase.list({ rarityId }, signal)).items;
    },
    enabled: Boolean(rarityId),
  });

export const adminCollectionDetailsOptions = (id: string) =>
  queryOptions({
    queryKey: [...catalogKey, 'admin-collection-details', id],
    queryFn: ({ signal }) => usecase.adminCollectionDetails(id, signal),
  });

export const eventGamesOptions = (historical = false) =>
  queryOptions({
    queryKey: [...catalogKey, 'event-games', historical],
    queryFn: ({ signal }) => usecase.eventGames(historical, signal),
    staleTime: 0,
  });

export const adminGamesOptions = (filters: GameFilters) =>
  queryOptions({
    queryKey: [...catalogKey, 'admin-games', filters],
    queryFn: ({ signal }) => usecase.adminGames(filters, signal),
  });

export const adminGameChoicesOptions = () =>
  queryOptions({
    queryKey: [...catalogKey, 'admin-game-choices'],
    queryFn: ({ signal }) => usecase.adminGameChoices(signal),
  });

export const createGameOptions = () =>
  mutationOptions({
    mutationFn: (input: GameInput) => usecase.createGame(input),
  });

export const updateGameOptions = () =>
  mutationOptions({
    mutationFn: ({ id, input }: { id: string; input: GameUpdate }) =>
      usecase.updateGame(id, input),
  });
